import type React from "react";
import { forwardRef } from "react";
import clsx from "clsx";
import styles from "./Button.module.scss";
import { Trigger as DropDownTrigger } from "@radix-ui/react-dropdown-menu";
import { Pressable as PopoverPressable } from "react-aria-components";
import type { LinkProps } from "react-router";
import { Link } from "react-router";

/* BASE */

type IBaseButtonStyles = {
  color?: "primary" | "negative" | "neutral" | "white";
  variant?: "filled" | "outlined" | "plain";
  size?: "medium" | "small" | "large";
  width?: "hug-content" | "fill-container";
  weight?: "regular" | "light";
  contentType?: "text" | "icon" | "text-and-icon";
};

const buttonClassName = ({
  color = "primary",
  variant = "filled",
  width = "hug-content",
  size = "medium",
  weight = "regular",
  contentType = "text",
}: IBaseButtonStyles) =>
  clsx(
    styles.baseButton,
    styles[`color-${color}`],
    styles[`width-${width}`],
    styles[`size-${size}`],
    styles[`weight-${weight}`],
    styles[`variant-${variant}`],
    styles[`contentType-${contentType}`],
  );

export type IBaseButton = IBaseButtonStyles &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    children: React.ReactNode;
    ref?: React.Ref<HTMLButtonElement>;
    asLinkTo?: never;
  };

/**
 * Given `asLinkTo`, a button renders as a link. It navigates, so assistive
 * technology should announce it as a link, and it can be opened in a new tab.
 */
type IBaseButtonLink = IBaseButtonStyles &
  StrictOmit<LinkProps, "className" | "to"> & {
    asLinkTo: LinkProps["to"];
    // A link can't be disabled or submit a form
    disabled?: never;
    type?: never;
  };

const BaseButton = ({
  children,
  color,
  variant,
  width,
  size,
  weight,
  contentType,
  ...props
}: IBaseButton | IBaseButtonLink) => {
  const className = buttonClassName({
    color,
    variant,
    width,
    size,
    weight,
    contentType,
  });

  if (props.asLinkTo !== undefined) {
    const { asLinkTo, ...linkProps } = props;
    return (
      <Link
        className={clsx(className, styles.link)}
        to={asLinkTo}
        {...linkProps}
      >
        {children}
      </Link>
    );
  }

  const { type = "button", ...buttonProps } = props;
  return (
    <button className={className} type={type} {...buttonProps}>
      {children}
    </button>
  );
};

/* STANDARD BUTTONS */

export type IFilledButton = StrictOmit<IBaseButton, "variant">;
export type IPlainButton = StrictOmit<IBaseButton, "variant">;
type IStandardButton =
  | StrictOmit<IBaseButton, "variant">
  | StrictOmit<IBaseButtonLink, "variant">;

export const FilledButton = (props: IStandardButton) => {
  return <BaseButton variant="filled" {...props} />;
};

export const OutlinedButton = (props: IStandardButton) => {
  return <BaseButton variant="outlined" {...props} />;
};

export const PlainButton = (props: IStandardButton) => {
  return <BaseButton variant="plain" {...props} />;
};

/* ICON BUTTON */

type IIconButtonLabel = { "aria-label": string };
export type IIconButton = StrictOmit<IBaseButton, "contentType"> &
  IIconButtonLabel;
type IIconButtonLink = StrictOmit<IBaseButtonLink, "contentType"> &
  IIconButtonLabel;

export const IconButton = ({
  variant = "plain",
  ...props
}: IIconButton | IIconButtonLink) => {
  return <BaseButton contentType="icon" variant={variant} {...props} />;
};

/**
 * DROPDOWN TRIGGER BUTTON
 *
 * This is a button that is used as a trigger for a dropdown menu: @/components/DropdownMenu/DropdownMenu.tsx
 *
 * @param param0
 * @returns
 */
export const DropDownTriggerButton = forwardRef<HTMLButtonElement, IBaseButton>(
  (
    {
      children,
      color,
      variant = "plain",
      width,
      size,
      weight,
      contentType,
      type = "button",
      ...props
    }: IBaseButton,
    ref,
  ) => {
    return (
      <DropDownTrigger
        className={buttonClassName({
          color,
          variant,
          width,
          size,
          weight,
          contentType,
        })}
        type={type}
        ref={ref}
        {...props}
      >
        {children}
      </DropDownTrigger>
    );
  },
);

DropDownTriggerButton.displayName = "Button";

/**
 * POPOVER TRIGGER BUTTON
 *
 * This is a button that is used as a trigger for a Popover Trigger: @/components/Popover/Popover.tsx
 *
 * @param param0
 * @returns
 */
export const PopoverTriggerButton = forwardRef<HTMLButtonElement, IBaseButton>(
  (
    {
      children,
      color,
      variant = "plain",
      width,
      size,
      weight,
      contentType,
      type = "button",
      ...props
    }: IBaseButton,
    ref,
  ) => {
    return (
      <PopoverPressable>
        <button
          className={buttonClassName({
            color,
            variant,
            width,
            size,
            weight,
            contentType,
          })}
          type={type}
          ref={ref}
          {...props}
        >
          {children}
        </button>
      </PopoverPressable>
    );
  },
);

PopoverTriggerButton.displayName = "Button";
